// sentry.js - Configuration centralisée de Sentry
import * as Sentry from "@sentry/react";
import React from 'react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from "react-router-dom";

const initSentry = () => {
    Sentry.init({
        dsn: "https://ade2b0eb65fc8c8b70cc559e7fc8ae86@o4509962798563328.ingest.de.sentry.io/4509962802298960",
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({
                // Capture 10% des sessions en mode replay
                sessionSampleRate: 0.1,
                // Capture 100% des sessions avec erreurs
                errorSampleRate: 1.0,
            }),
            // Intégration pour React Router
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
        ],

        // Environnement (à adapter selon votre déploiement)
        environment: process.env.NODE_ENV || 'development',

        // Taux d'échantillonnage des performances (ajuster en production)
        tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

        // URLs pour le tracing distribué
        tracePropagationTargets: [
            "localhost",
            /^https:\/\/.*\.ntrfinancial\.com/,
            /^https:\/\/parameter\.ntrfinancial\.com/,
            /^https:\/\/user\.ntrfinancial\.com/,
            /^https:\/\/recipient\.ntrfinancial\.com/,
            /^https:\/\/transfer\.ntrfinancial\.com/,
        ],

        // Filtrage des erreurs non pertinentes
        beforeSend(event) {
            // Ignorer les erreurs de réseau communes non critiques
            if (event.exception) {
                const error = event.exception.values[0];
                if (error && error.type === 'NetworkError' && error.value?.includes('Failed to fetch')) {
                    return null; // Ne pas envoyer cette erreur
                }
            }

            // Ignorer les erreurs d'extension de navigateur
            if (event.request?.url?.includes('extension://')) {
                return null;
            }

            return event;
        },

        // Tags globaux
        initialScope: {
            tags: {
                component: "ntr-financial-app",
            },
        },
    });
};

// Fonction utilitaire pour définir l'utilisateur dans Sentry
export const setSentryUser = (user) => {
    Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.username,
    });
};

// Fonction utilitaire pour ajouter du contexte
export const addSentryContext = (key, data) => {
    Sentry.setContext(key, data);
};

// Fonction utilitaire pour capturer une exception
export const captureSentryException = (error, context = {}) => {
    Sentry.withScope((scope) => {
        Object.keys(context).forEach(key => {
            scope.setTag(key, context[key]);
        });
        Sentry.captureException(error);
    });
};

// Fonction utilitaire pour capturer un message
export const captureSentryMessage = (message, level = 'info', context = {}) => {
    Sentry.withScope((scope) => {
        scope.setLevel(level);
        Object.keys(context).forEach(key => {
            scope.setTag(key, context[key]);
        });
        Sentry.captureMessage(message);
    });
};

export default initSentry;